import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Radio,
  Button,
  message,
  Row,
  Col,
  Card
} from 'antd';
import { UserOutlined, SaveOutlined } from '@ant-design/icons';
import axios from 'axios';
import * as serverconfig from '../../serverconn';
import moment from 'moment';

const { Option } = Select;
const { TextArea } = Input;

const PatientForm = ({ 
  visible, 
  onCancel, 
  onSuccess, 
  pendingPickupId, 
  patientData = null,
  isEdit = false 
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [nationalities, setNationalities] = useState([]);
  const [calculatedAge, setCalculatedAge] = useState(null);

  useEffect(() => {
    if (visible) {
      loadNationalities();
      if (isEdit && patientData) {
        // Pre-fill form with existing patient data
        const dateOfBirth = patientData.date_of_birth ? moment(patientData.date_of_birth) : null;
        const calculatedAge = dateOfBirth ? calculateAge(dateOfBirth) : 0;
        
        setCalculatedAge(calculatedAge);
        
        form.setFieldsValue({
          names: patientData.names,
          date_of_birth: dateOfBirth,
          age: calculatedAge, // Use calculated age instead of stored age
          nationality: patientData.nationality,
          occupation: patientData.occupation,
          sex: patientData.sex,
          patient_opd_ipd_number: patientData.patient_opd_ipd_number,
          national_id_number: patientData.national_id_number,
          village_of_residence: patientData.village_of_residence,
          village_of_workplace: patientData.village_of_workplace,
          physical_address: patientData.physical_address,
          phone_number: patientData.phone_number,
          email_address: patientData.email_address,
          lab_generated_number: patientData.lab_generated_number,
        });
      } else {
        // Reset form for new patient
        form.resetFields();
        setCalculatedAge(0);
      }
    }
  }, [visible, isEdit, patientData, form]);

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) {
      return 0;
    }
    
    const today = moment();
    const birthDate = moment(dateOfBirth);
    const age = today.diff(birthDate, 'years');
    
    return age >= 0 ? age : 0;
  };

  const handleDateOfBirthChange = (date) => {
    if (date) {
      const age = calculateAge(date);
      setCalculatedAge(age);
      form.setFieldsValue({ age: age });
    } else {
      setCalculatedAge(0);
      form.setFieldsValue({ age: 0 });
    }
  };

  const loadNationalities = async () => {
    try {
      // Comprehensive list of countries/nationalities
      const allCountries = [
        'Afghan', 'Albanian', 'Algerian', 'American', 'Andorran', 'Angolan', 'Antiguan', 'Argentine', 'Armenian', 'Australian', 'Austrian', 'Azerbaijani',
        'Bahamian', 'Bahraini', 'Bangladeshi', 'Barbadian', 'Belarusian', 'Belgian', 'Belizean', 'Beninese', 'Bhutanese', 'Bolivian', 'Bosnian', 'Botswanan', 'Brazilian', 'Bruneian', 'Bulgarian', 'Burkinabé', 'Burundian',
        'Cambodian', 'Cameroonian', 'Canadian', 'Cape Verdean', 'Central African', 'Chadian', 'Chilean', 'Chinese', 'Colombian', 'Comoran', 'Congolese', 'Costa Rican', 'Croatian', 'Cuban', 'Cypriot', 'Czech',
        'Danish', 'Djiboutian', 'Dominican', 'Dutch',
        'East Timorese', 'Ecuadorean', 'Egyptian', 'Emirati', 'English', 'Equatorial Guinean', 'Eritrean', 'Estonian', 'Ethiopian',
        'Fijian', 'Filipino', 'Finnish', 'French',
        'Gabonese', 'Gambian', 'Georgian', 'German', 'Ghanaian', 'Greek', 'Grenadian', 'Guatemalan', 'Guinean', 'Guyanese',
        'Haitian', 'Honduran', 'Hungarian',
        'Icelandic', 'Indian', 'Indonesian', 'Iranian', 'Iraqi', 'Irish', 'Israeli', 'Italian', 'Ivorian',
        'Jamaican', 'Japanese', 'Jordanian',
        'Kazakhstani', 'Kenyan', 'Kittian and Nevisian', 'Kuwaiti', 'Kyrgyz',
        'Laotian', 'Latvian', 'Lebanese', 'Liberian', 'Libyan', 'Liechtensteiner', 'Lithuanian', 'Luxembourger',
        'Macedonian', 'Malagasy', 'Malawian', 'Malaysian', 'Maldivan', 'Malian', 'Maltese', 'Marshallese', 'Mauritanian', 'Mauritian', 'Mexican', 'Micronesian', 'Moldovan', 'Monacan', 'Mongolian', 'Montenegrin', 'Moroccan', 'Mosotho', 'Motswana', 'Mozambican', 'Myanmar',
        'Namibian', 'Nauruan', 'Nepalese', 'New Zealander', 'Nicaraguan', 'Nigerian', 'Nigerien', 'North Korean', 'Northern Irish', 'Norwegian',
        'Omani',
        'Pakistani', 'Palauan', 'Panamanian', 'Papua New Guinean', 'Paraguayan', 'Peruvian', 'Polish', 'Portuguese', 'Qatari',
        'Romanian', 'Russian', 'Rwandan',
        'Saint Lucian', 'Salvadoran', 'Samoan', 'San Marinese', 'São Toméan', 'Saudi Arabian', 'Scottish', 'Senegalese', 'Serbian', 'Seychellois', 'Sierra Leonean', 'Singaporean', 'Slovakian', 'Slovenian', 'Solomon Islander', 'Somali', 'South African', 'South Korean', 'South Sudanese', 'Spanish', 'Sri Lankan', 'Sudanese', 'Surinamer', 'Swazi', 'Swedish', 'Swiss', 'Syrian',
        'Taiwanese', 'Tajikistani', 'Tanzanian', 'Thai', 'Togolese', 'Tongan', 'Trinidadian or Tobagonian', 'Tunisian', 'Turkish', 'Tuvaluan',
        'Ugandan', 'Ukrainian', 'Uruguayan', 'Uzbekistani',
        'Venezuelan', 'Vietnamese',
        'Welsh', 'Yemenite',
        'Zambian', 'Zimbabwean', 'Other'
      ];
      setNationalities(allCountries);
    } catch (error) {
      console.error('Error loading nationalities:', error);
    }
  };


  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // Format date of birth and clean up empty strings
      const formattedValues = {
        ...values,
        date_of_birth: values.date_of_birth ? values.date_of_birth.format('YYYY-MM-DD') : null,
      };

      // Convert empty strings to null for optional fields
      Object.keys(formattedValues).forEach(key => {
        if (formattedValues[key] === '') {
          formattedValues[key] = null;
        }
      });

      console.log('Sending patient data:', formattedValues);

      let response;
      if (isEdit && patientData) {
        // Update existing patient - don't include pending_sample_pickup in updates
        response = await axios.put(
          `${serverconfig.backendserverurl}/api/patients/${patientData.id}/`,
          formattedValues,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Token ${localStorage.getItem('token')}`
            }
          }
        );
        message.success('Patient updated successfully!');
      } else {
        // Create new patient - include pending_sample_pickup for new patients
        const createValues = {
          ...formattedValues,
          pending_sample_pickup: pendingPickupId
        };
        response = await axios.post(
          `${serverconfig.backendserverurl}/api/patients/`,
          createValues,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Token ${localStorage.getItem('token')}`
            }
          }
        );
        message.success('Patient created successfully!');
      }

      onSuccess(response.data);
      form.resetFields();
      
    } catch (error) {
      console.error('Error saving patient:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = 'Failed to save patient';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.non_field_errors) {
          errorMessage = error.response.data.non_field_errors.join(', ');
        } else {
          // Handle field-specific errors
          const fieldErrors = Object.keys(error.response.data).map(field => 
            `${field}: ${Array.isArray(error.response.data[field]) ? error.response.data[field].join(', ') : error.response.data[field]}`
          );
          errorMessage = fieldErrors.join('; ');
        }
      }
      
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <UserOutlined style={{ marginRight: 8 }} />
          {isEdit ? 'Edit Patient Information' : 'Add Patient Information'}
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width="95%"
      style={{ maxWidth: 800 }}
      destroyOnClose
    >
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            sex: 'Male'
          }}
        >
          <Row gutter={16}>
            {/* Names - Required */}
            <Col span={12}>
              <Form.Item
                name="names"
                label="Names *"
                rules={[{ required: true, message: 'Please enter patient names' }]}
              >
                <Input placeholder="Enter full names" />
              </Form.Item>
            </Col>


            {/* Date of Birth - Required */}
            <Col span={12}>
              <Form.Item
                name="date_of_birth"
                label="Date of Birth *"
                rules={[{ required: true, message: 'Please select date of birth' }]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  format="DD/MM/YYYY"
                  placeholder="Select date of birth"
                  onChange={handleDateOfBirthChange}
                />
              </Form.Item>
            </Col>

            {/* Age - Auto-calculated */}
            <Col span={12}>
              <Form.Item
                name="age"
                label="Age (Auto-calculated)"
                tooltip="Age is automatically calculated from date of birth"
              >
                <Input 
                  type="number" 
                  placeholder="Auto-calculated" 
                  disabled
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  suffix={<span style={{ color: '#999' }}>years</span>}
                />
              </Form.Item>
            </Col>

            {/* Nationality - Required */}
            <Col span={12}>
              <Form.Item
                name="nationality"
                label="Nationality *"
                rules={[{ required: true, message: 'Please select nationality' }]}
              >
                <Select 
                  placeholder="Select Nationality"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  optionFilterProp="children"
                >
                  {nationalities.map(nationality => (
                    <Option key={nationality} value={nationality}>
                      {nationality}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* Sex - Required */}
            <Col span={12}>
              <Form.Item
                name="sex"
                label="Sex *"
                rules={[{ required: true, message: 'Please select sex' }]}
              >
                <Radio.Group>
                  <Radio value="Male">Male</Radio>
                  <Radio value="Female">Female</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>

            {/* Occupation - Optional */}
            <Col span={12}>
              <Form.Item
                name="occupation"
                label="Occupation"
              >
                <Input placeholder="Enter occupation (optional)" />
              </Form.Item>
            </Col>

            {/* Patient OPD/IPD Number - Optional */}
            <Col span={12}>
              <Form.Item
                name="patient_opd_ipd_number"
                label="Patient OPD/IPD Number"
              >
                <Input placeholder="Enter OPD/IPD number" />
              </Form.Item>
            </Col>

            {/* National ID Number - Optional */}
            <Col span={12}>
              <Form.Item
                name="national_id_number"
                label="National Identification No (NIN)"
              >
                <Input placeholder="Enter NIN" />
              </Form.Item>
            </Col>

            {/* Village of Residence - Optional */}
            <Col span={12}>
              <Form.Item
                name="village_of_residence"
                label="Village of Residence"
              >
                <Input placeholder="Enter village of residence" />
              </Form.Item>
            </Col>

            {/* Village of Workplace - Optional */}
            <Col span={12}>
              <Form.Item
                name="village_of_workplace"
                label="Village of Workplace"
              >
                <Input placeholder="Enter village of workplace" />
              </Form.Item>
            </Col>

            {/* Physical Address - Optional */}
            <Col span={24}>
              <Form.Item
                name="physical_address"
                label="Physical Address"
              >
                <TextArea rows={2} placeholder="Enter physical address" />
              </Form.Item>
            </Col>

            {/* Phone Number - Optional */}
            <Col span={12}>
              <Form.Item
                name="phone_number"
                label="Phone Number"
              >
                <Input placeholder="Enter phone number" />
              </Form.Item>
            </Col>

            {/* Email Address - Optional */}
            <Col span={12}>
              <Form.Item
                name="email_address"
                label="Email Address"
              >
                <Input placeholder="Enter email address" />
              </Form.Item>
            </Col>

            {/* Lab Generated Number - Optional */}
            <Col span={24}>
              <Form.Item
                name="lab_generated_number"
                label="Lab Generated Number"
              >
                <Input placeholder="CHI___ or FHL___" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={handleCancel} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              icon={<SaveOutlined />}
            >
              {isEdit ? 'Update Patient' : 'Save Patient'}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </Modal>
  );
};

export default PatientForm;
